import React, { useState, useEffect } from "react";
import vCardsJS from "vcards-js";
import axios from "axios";

export default function SaveContact(props) {
  // Store the preloaded image base64 string in state
  const [preloadedImg, setPreloadedImg] = useState(null);

  // Preload the image as soon as the component loads or the image URL changes
  useEffect(() => {
    const preloadImage = async () => {
      if (props.data?.mainUserImg) {
        try {
          const { data } = await axios({
            method: "GET",
            url: props.data.mainUserImg,
            responseType: "blob",
            timeout: 5000 // Prevents blocking if the host stalls
          });

          // CRITICAL iOS FIX: If the image file size is too big (>200KB), iOS will reject it.
          if (data.size > 200000) {
            console.warn("Image exceeds 200KB. iOS will drop large image vCards.");
          }

          const reader = new FileReader();
          reader.readAsDataURL(data);
          reader.onloadend = () => {
            const base64data = reader.result.split(",")[1]; // Get only the base64 string
            setPreloadedImg(base64data);
          };
        } catch (error) {
          console.error("Failed to preload contact image:", error);
        }
      }
    };

    preloadImage();
  }, [props.data?.mainUserImg]);

  const downloadVcfFile = (vcfText) => {
    try {
      // Create a native Blob. This uses less RAM and bypasses strict data URL filters.
      const blob = new Blob([vcfText], { type: "text/vcard;charset=utf-8;" });
      const blobUrl = window.URL.createObjectURL(blob);

      const element = document.createElement("a");
      element.href = blobUrl;
      element.download = "myContact.vcf";

      document.body.appendChild(element);
      element.click();
      
      // Cleanup memory instantly
      document.body.removeChild(element);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download handling error:", error);
    }
  };

  // This function is now fully SYNCHRONOUS because the image data is already in memory
  const CreateVCard = () => {
    try {
      const vCard = vCardsJS();
      vCard.version = '3.0'; // Best version compatibility for iOS Contacts
      
      vCard.firstName = props.data?.fullName || "Contact";
      vCard.organization = '';
      vCard.title = '';

      if (props.data?.email?.[0]?.link) vCard.email = props.data.email[0].link;
      if (props.data?.mobileNumber?.[0]?.link) vCard.workPhone = props.data.mobileNumber[0].link;
      if (props.data?.website?.[0]?.link) vCard.url = props.data.website[0].link;

      // Embed the image instantly from preloaded state with zero delay
      if (preloadedImg) {
        vCard.photo.embedFromString(preloadedImg, "JPEG");
      }

      let vcfText = vCard.getFormattedString();
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      const additionalFields = [];
      if (Array.isArray(props.data?.mobileNumber)) {
        props.data.mobileNumber.forEach((phone, idx) => {
          if (phone?.link && idx > 0) additionalFields.push(`TEL;TYPE=CELL:${phone.link}`);
        });
      }
      if (Array.isArray(props.data?.email)) {
        props.data.email.forEach((email, idx) => {
          if (email?.link && idx > 0) additionalFields.push(`EMAIL;TYPE=INTERNET:${email.link}`);
        });
      }
      if (Array.isArray(props.data?.website)) {
        props.data.website.forEach((web, idx) => {
          if (web?.link && idx > 0) additionalFields.push(`URL;TYPE=website:${web.link}`);
        });
      }

      const platforms = [
        { key: 'facebook', label: 'facebook' },
        { key: 'instgram', label: 'instagram' },
        { key: 'linkedIn', label: 'linkedin' },
        { key: 'snapChat', label: 'snapchat' },
        { key: 'whatsApp', label: 'whatsapp' },
        { key: 'telegram', label: 'telegram' },
        { key: 'twiter', label: 'twitter' },
        { key: 'tikTok', label: 'tiktok' }
      ];

      platforms.forEach(platform => {
        if (Array.isArray(props.data?.[platform.key])) {
          props.data[platform.key].forEach((item) => {
            if (item?.link) {
              additionalFields.push(`X-SOCIALPROFILE;TYPE=${platform.label}:${item.link}`);
              if (!isIOS) additionalFields.push(`URL;TYPE=${platform.label}:${item.link}`);
            }
          });
        }
      });

      if (additionalFields.length > 0) {
        vcfText = vcfText.replace(/(\r?\n)END:VCARD(\r?\n)?$/i, `\r\n${additionalFields.join('\r\n')}\r\nEND:VCARD\r\n`);
      }

      return vcfText;
    } catch (error) {
      console.error('Error generating VCard:', error);
      return null;
    }
  };

  return (
    <button
      style={{ backgroundColor: props.color }}
      // Instant execution: No "async/await" lag to trigger browser security blockers
      onClick={() => {
        const vcfContent = CreateVCard();
        if (vcfContent) {
          downloadVcfFile(vcfContent);
        }
      }}
      className="save-button"
    >
      SAVE CONTACT
    </button>
  );
}
